import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from './info';
import { setAccessToken, clearAccessToken } from './api';

const supabaseUrl = `https://${projectId}.supabase.co`;

// Singleton Supabase client — survives Vite HMR reloads
const key = '__supabase_client';
export const supabase: SupabaseClient =
  (globalThis as any)[key] ??
  ((globalThis as any)[key] = createClient(supabaseUrl, publicAnonKey));

function getUserMetadata(user: any) {
  return user?.user_metadata ?? {};
}

export function isPendingCoachAuthUser(user: any): boolean {
  const metadata = getUserMetadata(user);
  return metadata.role === 'coach' && metadata.verificationStatus === 'pending';
}

// Sign in with email and password
export async function signIn(email: string, password: string) {
  try {
    console.log('🔐 Calling Supabase signInWithPassword for:', email);
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('❌ Supabase auth error:', error.message);
      throw new Error(error.message);
    }

    if (!data.session) {
      console.error('❌ No session returned from Supabase');
      throw new Error('No session returned from authentication');
    }

    if (isPendingCoachAuthUser(data.user)) {
      clearAccessToken();
      await supabase.auth.signOut().catch(() => null);
      throw new Error('Coach account is pending admin verification. Please wait for the approval email before logging in.');
    }

    if (data.session?.access_token) {
      console.log('✅ Access token received and stored');
      setAccessToken(data.session.access_token);
      
      // Verify token was stored
      const storedToken = localStorage.getItem('access_token');
      if (!storedToken) {
        console.error('❌ Token was not stored in localStorage!');
      }
    } else {
      console.warn('⚠️ No access token in session');
    }

    console.log('✅ Sign in successful');
    return { data, error: null };
  } catch (error: any) {
    console.error('❌ Sign in error:', error.message);
    return { data: null, error };
  }
}

// Sign out
export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();

    // Always clear the access token, even if signOut fails
    clearAccessToken();

    // Ignore "Auth session missing!" errors - this happens when token is already invalid
    if (error && !error.message.includes('session missing')) {
      throw new Error(error.message);
    }
    
    return { error: null };
  } catch (error: any) {
    // Still clear token on error
    clearAccessToken();
    console.error('Sign out error:', error);
    return { error };
  }
}

// Get current session (also refreshes if needed)
export async function getSession() {
  try {
    // This will automatically refresh the session if it's expired
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      throw new Error(error.message);
    }

    // Update stored token if we have a valid session
    if (data.session?.access_token) {
      setAccessToken(data.session.access_token);
    } else {
      // No valid session, clear any stale token
      clearAccessToken();
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Get session error:', error);
    clearAccessToken();
    return { data: null, error };
  }
}

// Explicitly refresh the session
export async function refreshSession() {
  try {
    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      throw new Error(error.message);
    }

    if (data.session?.access_token) {
      setAccessToken(data.session.access_token);
      console.log('✅ Session refreshed successfully');
    } else {
      clearAccessToken();
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Refresh session error:', error);
    clearAccessToken();
    return { data: null, error };
  }
}

// Check if user is authenticated
export async function isAuthenticated(): Promise<boolean> {
  const { data } = await getSession();
  return !!data?.session;
}